import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { upcomingEvents } from "@/mocks/events";

const slugMap: Record<number, string> = {
  1: "glamping-party",
  2: "book-club",
  3: "social-game",
  4: "family-camp",
};

export default function UpcomingEvents() {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const navigate = useNavigate();

  return (
    <section id="upcoming-events" className="py-20 md:py-28 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-red-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Upcoming Events
            </p>
            <h2
              className="text-3xl md:text-5xl font-black text-gray-900"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              即將展開的冒險
            </h2>
          </div>
          <p className="text-gray-500 text-base md:text-right max-w-xs leading-relaxed">
            每一場活動都是精心策劃的生活提案
          </p>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {upcomingEvents.map((event) => (
            <div
              key={event.id}
              className="relative rounded-2xl overflow-hidden cursor-pointer group"
              style={{ aspectRatio: "3/4" }}
              onMouseEnter={() => setHoveredId(event.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate(`/event/${slugMap[event.id]}`)}
            >
              {/* Image */}
              <img
                src={event.image}
                alt={event.title}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />

              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

              {/* Category Badge */}
              <div className="absolute top-4 right-4">
                <span className={`${event.categoryColor} text-white text-xs font-semibold px-3 py-1 rounded-full`}>
                  {event.category}
                </span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-white font-bold text-lg leading-tight mb-1">
                  {event.title}
                </h3>
                <p className="text-white/70 text-xs mb-3">{event.subtitle}</p>

                <div className="flex items-center gap-1.5 text-white/70 text-xs mb-1">
                  <i className="ri-calendar-line" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center gap-1.5 text-white/70 text-xs mb-3">
                  <i className="ri-map-pin-line" />
                  <span>{event.location}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-orange-400 text-xs font-medium">
                    剩餘 {event.spotsLeft} 名額
                  </span>
                  <span className="text-white font-bold text-sm">{event.price}</span>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {event.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-white/15 text-white/80 text-xs px-2 py-0.5 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <button className="mt-4 w-full py-2.5 rounded-full bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-all duration-200 cursor-pointer whitespace-nowrap opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                  立即報名
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View More */}
        <div className="text-center mt-12">
          <a
            href="https://www.instagram.com/rappy.meme/"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 text-red-600 font-semibold text-base hover:gap-4 transition-all duration-200 cursor-pointer group"
          >
            追蹤 IG 掌握最新活動
            <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
